import { Head, Link, router } from "@inertiajs/react"
import { Plus, RefreshCw, Settings2 } from "lucide-react"
import { useState } from "react"

import { AppShell } from "@/components/AppShell"
import { AddLineupProductDialog } from "@/components/bids/AddLineupProductDialog"
import { BidSettingsDialog } from "@/components/bids/BidSettingsDialog"
import {
  BidStatusBadge,
  PRICING_FORMAT_LABELS,
  WASTE_HANDLING_LABELS,
} from "@/components/bids/BidStatusBadge"
import { BidSubNav } from "@/components/bids/BidSubNav"
import { CategoryPricingTable } from "@/components/bids/CategoryPricingTable"
import { EditableCell } from "@/components/bids/EditableCell"
import { LevelDialog } from "@/components/bids/LevelDialog"
import { LevelSummaryCards } from "@/components/bids/LevelSummaryCards"
import { MarginWarningBanner } from "@/components/bids/MarginWarningBanner"
import { StalenessBanner } from "@/components/bids/StalenessBanner"
import { TabStrip } from "@/components/bids/TabStrip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { pct, rate } from "@/lib/money"
import { pluralize } from "@/lib/relative-time"
import type { WorkspaceCategory, WorkspaceLevel, WorkspaceProps } from "@/types/bids"

export default function Workspace(props: WorkspaceProps) {
  const { community, bid, plan_count, sync_status, categories } = props
  const [activeKey, setActiveKey] = useState(categories[0]?.key)
  const [levelDialogOpen, setLevelDialogOpen] = useState(false)
  const [editingLevel, setEditingLevel] = useState<WorkspaceLevel | null>(null)
  const [productDialogOpen, setProductDialogOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const active = categories.find((c) => c.key === activeKey) ?? categories[0]
  const description = `Bid workspace for ${community.name} — levels, lineup, and quoted prices.`

  const refreshCosts = () => {
    setRefreshing(true)
    router.post(bid.urls.refresh_costs, {}, {
      preserveScroll: true,
      onFinish: () => setRefreshing(false),
    })
  }

  return (
    <>
      <Head title={`${community.name} · Bid`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${community.name} · Bid`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <p className="text-sm text-ink-muted">
          <Link href="/bids" className="hover:text-ink-body">
            Bids
          </Link>{" "}
          / <strong>{community.name}</strong>
        </p>

        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-3">
              {community.name}
              <BidStatusBadge status={bid.status} rebidNeeded={bid.rebid_needed} />
            </h1>
            <p className="mt-1 text-ink-muted">
              {community.customer.name}
              {community.locality && ` · ${community.locality}`} · {bid.bid_number}
            </p>
            <p className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <Badge tone="muted">{PRICING_FORMAT_LABELS[community.customer.pricing_format]}</Badge>
              <Badge tone="muted">
                Quantities {WASTE_HANDLING_LABELS[community.customer.waste_handling]}
              </Badge>
              <span className="text-ink-muted">
                {pluralize(plan_count, "plan")} · {sync_status.length} takeoff
                {sync_status.length === 1 ? "" : "s"} linked · use tax {pct(bid.use_tax_pct)}
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={refreshCosts}
              disabled={refreshing}
              data-testid="refresh-costs"
            >
              <RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh costs
            </Button>
            <Button
              variant="ghost"
              onClick={() => setSettingsOpen(true)}
              aria-label="Bid settings"
              data-testid="bid-settings"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <StalenessBanner reasons={bid.stale_reasons} />

        <BidSubNav
          active="workspace"
          urls={{ workspace: bid.urls.update, matrix: bid.urls.matrix, sheet: bid.urls.sheet }}
        />

        <TabStrip className="mt-4" testid="bid-category-tabs">
          {categories.map((category) => (
            <button
              key={category.key}
              onClick={() => setActiveKey(category.key)}
              className={`whitespace-nowrap border-b-2 pb-2.5 font-medium ${
                active?.key === category.key
                  ? "border-accent text-ink-display"
                  : "border-transparent text-ink-muted hover:text-ink-body"
              }`}
              data-testid={`bid-category-${category.key}`}
            >
              {category.label}
              {category.levels.length > 0 && (
                <span className="ml-1.5 font-normal text-ink-muted">
                  · {category.levels.length}
                </span>
              )}
            </button>
          ))}
        </TabStrip>

        {active && (
          <div className="mt-5 space-y-5">
            <CategoryHeader category={active} />
            {active.levels.map((level) => (
              <MarginWarningBanner key={level.id} level={level} />
            ))}
            <LevelSummaryCards
              levels={active.levels}
              onAddLevel={() => {
                setEditingLevel(null)
                setLevelDialogOpen(true)
              }}
              onEditLevel={(level) => {
                setEditingLevel(level)
                setLevelDialogOpen(true)
              }}
            />
            <CategoryPricingTable category={active} />
            <Button
              variant="soft"
              onClick={() => setProductDialogOpen(true)}
              disabled={active.levels.length === 0}
              data-testid={`add-product-${active.key}`}
            >
              <Plus className="mr-1.5 h-4 w-4" /> Add product
            </Button>
            {active.levels.length === 0 && (
              <p className="text-sm text-ink-muted">
                Add a level first — products hang off levels, and the level&apos;s quoted price
                covers all of them.
              </p>
            )}
          </div>
        )}

        {active && (
          <>
            <LevelDialog
              open={levelDialogOpen}
              onOpenChange={setLevelDialogOpen}
              category={active.key}
              categoryLabel={active.label}
              addUrl={active.urls.add_level}
              level={editingLevel}
            />
            <AddLineupProductDialog
              open={productDialogOpen}
              onOpenChange={setProductDialogOpen}
              category={active}
              addUrl={active.urls.add_product}
            />
          </>
        )}
        <BidSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          bid={bid}
          customer={community.customer}
        />
      </AppShell>
    </>
  )
}

/** The per-category knob row: assumed waste % (COGS input) and the
 *  order-template pin with its frozen adder. */
function CategoryHeader({ category }: { category: WorkspaceCategory }) {
  const repin = (versionId: number) =>
    router.patch(
      category.urls.update,
      { bid_category: { order_template_version_id: versionId } },
      { preserveScroll: true }
    )

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink-muted">
      <span className="inline-flex items-center gap-1">
        Waste
        <EditableCell
          value={category.waste_pct}
          display={pct(category.waste_pct)}
          onCommit={(raw) =>
            router.patch(
              category.urls.update,
              { bid_category: { waste_pct: raw } },
              { preserveScroll: true }
            )
          }
          testid={`waste-cell-${category.key}`}
        />
      </span>

      {category.pin ? (
        <span className="inline-flex items-center gap-2" data-testid={`template-chip-${category.key}`}>
          <Link href={category.template_url} className="hover:text-ink-body">
            {category.label} template v{category.pin.version} ·{" "}
            {rate(category.pin.adder_per_sf, "SF")} ↗
          </Link>
          {category.pin.stale && category.pin.current_version_id != null && (
            <>
              <Badge tone="signal">v{category.pin.current_version} available</Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => repin(category.pin!.current_version_id!)}
                data-testid={`repin-${category.key}`}
              >
                Update pin
              </Button>
            </>
          )}
        </span>
      ) : (
        <span className="inline-flex items-center gap-2" data-testid={`template-chip-${category.key}`}>
          <Link href={category.template_url} className="hover:text-ink-body">
            No template pinned — adder $0.00/SF ↗
          </Link>
          {category.unpinned_current_version_id != null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => repin(category.unpinned_current_version_id!)}
              data-testid={`repin-${category.key}`}
            >
              Pin current
            </Button>
          )}
        </span>
      )}
    </div>
  )
}
